/** @format */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { FudisSelectOption } from '@funidata/ngx-fudis';
import { TranslocoService } from '@ngneat/transloco';
import { Code, SuomiFiUserBasicDetails, Urn } from 'common-typescript/types';
import { LocaleService } from 'sis-common';
import { AppErrorHandler, CommonCodeService } from 'sis-components';
import { SuomiFiUserDetailsForm } from '../../service/suomi-fi-user-details-form.service';

type CodeFieldKey = 'citizenshipUrn' | 'municipalityUrn' | 'motherTongueUrn' | 'genderUrn' | 'preferredLanguageUrn';

@Component({
    selector: 'app-suomi-fi-user-details-form-fields',
    templateUrl: './suomi-fi-user-details-form-fields.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SuomiFiUserDetailsFormFieldsComponent implements OnInit {
    @Input({ required: true }) form: FormGroup<SuomiFiUserDetailsForm>;
    @Input({ required: true }) suomiFiUserBasicDetails: SuomiFiUserBasicDetails;

    private readonly commonCodeService = inject(CommonCodeService);
    private readonly localeService = inject(LocaleService);
    private readonly translocoService = inject(TranslocoService);
    private readonly errorHandler = inject(AppErrorHandler);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);

    citizenshipOptions: FudisSelectOption<Urn>[] = [];
    municipalityOptions: FudisSelectOption<Urn>[] = [];
    motherTongueOptions: FudisSelectOption<Urn>[] = [];
    genderOptions: FudisSelectOption<Urn>[] = [];
    preferredLanguageOptions: FudisSelectOption<Urn>[] = [];

    ngOnInit(): void {
        this.createOptions(
            'urn:code:country',
            'citizenshipUrn',
            options => (this.citizenshipOptions = options),
            this.suomiFiUserBasicDetails.citizenshipUrns?.[0],
        );
        this.createOptions(
            'urn:code:municipality',
            'municipalityUrn',
            options => (this.municipalityOptions = options),
            this.suomiFiUserBasicDetails.municipalityUrn,
        );
        this.createOptions(
            'urn:code:language',
            'motherTongueUrn',
            options => (this.motherTongueOptions = options),
            this.suomiFiUserBasicDetails.motherTongueUrn,
        );
        this.createOptions(
            'urn:code:gender',
            'genderUrn',
            options => (this.genderOptions = options),
            this.suomiFiUserBasicDetails.genderUrn,
        );
        this.createOptions(
            'urn:code:preferred-language',
            'preferredLanguageUrn',
            options => (this.preferredLanguageOptions = options),
            this.suomiFiUserBasicDetails.preferredLanguageUrn ?? this.getCurrentLanguageUrn(),
        );
    }

    private createOptions(
        codeBookUrn: Urn,
        fieldKey: CodeFieldKey,
        setOptions: (options: FudisSelectOption<Urn>[]) => void,
        initialValue: Urn | undefined,
    ): void {
        this.commonCodeService
            .getGroupedCodesObservable(codeBookUrn)
            .pipe(this.errorHandler.defaultErrorHandler(), takeUntilDestroyed(this.destroyRef))
            .subscribe(groupedCodes => {
                const universityOptions = Object.values(groupedCodes.universityCodes)
                    .map((code: Code) => this.codeToOption(code))
                    .sort((a, b) => a.label.localeCompare(b.label, this.localeService.getCurrentLanguage()));
                let options = [...universityOptions];
                if (!groupedCodes.hideOtherCodes) {
                    const otherOptions = Object.values(groupedCodes.otherCodes)
                        .map((code: Code) => this.codeToOption(code))
                        .sort((a, b) => a.label.localeCompare(b.label, this.localeService.getCurrentLanguage()));
                    options = [...options, ...otherOptions];
                }
                setOptions(options);
                this.setInitialValue(fieldKey, initialValue, options);
                this.changeDetectorRef.markForCheck();
            });
    }

    private setInitialValue(fieldKey: CodeFieldKey, initialUrn: Urn | undefined, options: FudisSelectOption<Urn>[]): void {
        if (!initialUrn || this.form.controls[fieldKey].value) {
            return;
        }

        this.form.controls[fieldKey].setValue(options.find(option => option.value === initialUrn) ?? null);
    }

    private getCurrentLanguageUrn(): Urn {
        return `urn:code:preferred-language:${this.localeService.getCurrentLanguage()}`;
    }

    private codeToOption(code: Code): FudisSelectOption<Urn> {
        const label = [
            this.localeService.localize(code.name),
            code.universitySpecifier ? `(${code.universitySpecifier})` : null,
            code.deprecated ? this.translocoService.translate('SIS_COMPONENTS.SELECT.DEPRECATED') : null,
        ]
            .filter(Boolean)
            .join(' ');
        return {
            value: code.urn,
            label,
            disabled: !!code.deprecated,
        };
    }
}
